import React, {FC} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import ModalTypes from '../../enums/app/modalTypes';
import useIsErrorPage from '../../hooks/useIsErrorPage';
import {selectGlobalModals} from '../../state/selectors/modal';
import {IModal} from '../../state/types/modal';
import DeviceInSblModePopup from './DeviceInSblModePopup/DeviceInSblModePopup';
import DeviceLockedPopup from './DeviceLockedPopup/DeviceLockedPopup';
import FirmwareReleaseNotesPopup from './FirmwareReleaseNotesPopup/FirmwareReleaseNotesPopup';
import FWUAdditionalStepsGuidePopup from './FWUAdditionalStepsGuidePopup/FWUAdditionalStepsGuidePopup';
import FWUErrorPopup from './FWUErrorPopup/FWUErrorPopup';
import FWULedCheckPopup from './FWULedCheckPopup/FWULedCheckPopup';
import HidReconnectPopup from './HidReconnectPopup/HidReconnectPopup';
import LeviaFwuPopup from './Levia/LeviaFwuPopup';
import LeviaLearnMorePopup from './Levia/LeviaLearnMorePopup';
import LoyaltyClubPopup from './LoyaltyClubPopup/LoyaltyClubPopup';
import ManualRegistrationPopup from './ManualRegistrationPopup/ManualRegistrationPopup';
import OutsideClickPopup from './OutsideClickPopup/OutsideClickPopup';
import OutsideRegistrationPopup from './OutsideRegistrationPopup/OutsideRegistrationPopup';
import PairingGuideErrorPopup from './PairingGuideErrorPopup/PairingGuideErrorPopup';
import PairingSkipPopup from './PairingSkipPopup/PairingSkipPopup';
import PauseModePopup from './PauseModePopup/PauseModePopup';
import PurchaseDateFullPagePopup from './PurchaseDateFullPagePopup/PurchaseDateFullPagePopup';
import QuickStartGuidePopup from './QuickStartGuidePopup/QuickStartGuidePopup';
import ReconnectDeviceGuidePopup from './ReconnectDeviceGuidePopup/ReconnectDeviceGuidePopup';
import RegistrationFailedPopup from './RegistrationFailedPopup/RegistrationFailedPopup';
import RegistrationHolderPopup from './RegistrationHolderPopup/RegistrationHolderPopup';
import ConsumerFeedbackGatheringPopup from './SuccessfulDeviceConnectionPopup/ConsumerFeedbackGatheringPopup';
import UsbRequiredPopup from './UsbRequiredPopup/UsbRequiredPopup';
import UWMUnavailabilityPopup from './UWMUnavailabilityPopup/UWMUnavailabilityPopup';

const MODAL_COMPONENTS = {
    [ModalTypes.PAIRING_GUIDE_ERROR]: PairingGuideErrorPopup,
    [ModalTypes.PAIRING_SKIP]: PairingSkipPopup,
    [ModalTypes.REGISTRATION_FAILED]: RegistrationFailedPopup,
    [ModalTypes.HID_RECONNECT]: HidReconnectPopup,
    [ModalTypes.RECONNECT_DEVICE_GUIDE]: ReconnectDeviceGuidePopup,
    [ModalTypes.FIRMWARE_RELEASE_NOTES_POPUP]: FirmwareReleaseNotesPopup,
    [ModalTypes.FWU_LED_CHECK]: FWULedCheckPopup,
    [ModalTypes.FWU_ADDITIONAL_STEPS_GUIDE]: FWUAdditionalStepsGuidePopup,
    [ModalTypes.FWU_ERROR]: FWUErrorPopup,
    [ModalTypes.REGISTRATION_HOLDER]: RegistrationHolderPopup,
    [ModalTypes.PURCHASE_DATE]: PurchaseDateFullPagePopup,
    [ModalTypes.DEVICE_LOCKED]: DeviceLockedPopup,
    [ModalTypes.USB_REQUIRED]: UsbRequiredPopup,
    [ModalTypes.MANUAL_REGISTRATION]: ManualRegistrationPopup,
    [ModalTypes.UWM_UNAVAILABILITY]: UWMUnavailabilityPopup,
    [ModalTypes.OUTSIDE_REGISTRATION]: OutsideRegistrationPopup,
    [ModalTypes.OUTSIDE_CLICK_POPUP]: OutsideClickPopup,
    [ModalTypes.LOYALTY_CLUB]: LoyaltyClubPopup,
    [ModalTypes.PAUSE_MODE]: PauseModePopup,
    [ModalTypes.LEVIA_LEAR_MORE]: LeviaLearnMorePopup,
    [ModalTypes.LEVIA_FWU]: LeviaFwuPopup,
    [ModalTypes.DEVICE_IS_IN_SBL]: DeviceInSblModePopup,
    [ModalTypes.QUICK_START_GUIDE_POPUP]: QuickStartGuidePopup,
    [ModalTypes.CONSUMER_FEEDBACK_GATHERING]: ConsumerFeedbackGatheringPopup,
};

const ModalsRoot: FC = () => {
    const modals = useSelector(selectGlobalModals);
    const dispatch = useDispatch();
    const isErrorPage = useIsErrorPage();

    if (isErrorPage) return null;
    if (!modals.length) return null;

    const alwaysDisplayModals = modals.filter((m) => m.alwaysDisplay);
    const modalsSorted = [...modals].sort((a, b) => b.order - a.order);
    const curModal = modalsSorted[modalsSorted.length - 1];

    return (
        <>
            <CurrentModal curModal={curModal} />
            {alwaysDisplayModals.map((modal: {modalType: ModalTypes; modalProps: any}, index: number) => {
                const {modalType, modalProps} = modal;
                const ComponentModal = MODAL_COMPONENTS[modalType];

                return <ComponentModal key={modalType} {...modalProps} modalType={modalType} dispatch={dispatch} />;
            })}
        </>
    );
};

const CurrentModal: FC<{curModal: IModal}> = ({curModal}) => {
    const dispatch = useDispatch();

    if (!curModal) return null;

    const {modalType, modalProps, alwaysDisplay} = curModal;

    if (alwaysDisplay) return null;

    const SpecificModal = MODAL_COMPONENTS[modalType];

    return <SpecificModal key={modalType} {...modalProps} modalType={modalType} dispatch={dispatch} />;
};

export default ModalsRoot;
